import type { MediaItem, SharedData } from '@/types/feed.type'

import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useRouter } from '@/routes/hooks/use-router'
import { MessageCircle, Heart, Share2, OctagonX } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

export interface FeedSharedContentProps {
  contentId: string
  sharedData: SharedData
  isProgram?: boolean
}

/**
 * แสดงเนื้อหา Shared Content (Feed/Article/Livestream)
 */
export default function FeedSharedContent({
  contentId,
  sharedData,
  isProgram,
}: FeedSharedContentProps) {
  const router = useRouter()

  const {
    userName,
    userImageUrl,
    firstName,
    lastName,
    createdAt,
    content,
    commentCount,
    likeCount,
    shareCount,
    status,
    mediaItems: mediaItemsData,
  } = sharedData

  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const mediaItems: MediaItem[] =
    (mediaItemsData as MediaItem[] | undefined) || []

  const currentUserName =
    firstName && lastName ? `${firstName} ${lastName}` : userName || 'Unknown'

  if (status === 'deleted') {
    return (
      <Card className="mt-4 p-4 bg-white border border-gray-200 relative text-gray-600 shadow-sm">
        <div className="flex items-center gap-2">
          <OctagonX /> <span className="text-2xl">โพสนี้ถูกลบไปแล้ว</span>
        </div>
      </Card>
    )
  }

  return (
    <Card
      className="mt-4 p-4 bg-white border border-gray-200 relative shadow-sm"
      onClick={() => {
        if (!isProgram) router.push(`/feeds/${contentId}/detail`)
      }}
    >
      <div className="flex items-center mb-2">
        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mr-2">
          {/* user image */}
          <Avatar className="size-8 ring-1 ring-neutral-600">
            <AvatarImage
              src={userImageUrl}
              alt={currentUserName || ''}
              className="object-cover"
            />
            {currentUserName && (
              <AvatarFallback className="size-8">
                {currentUserName.slice(0, 2)}
              </AvatarFallback>
            )}
          </Avatar>
        </div>
        <div>
          <div className="font-medium text-sm text-gray-900">
            {currentUserName}
          </div>
          <div className="text-xs text-gray-500">
            {new Date(createdAt?._seconds * 1000).toLocaleString()}
          </div>
        </div>
      </div>
      <div className="mb-2">
        <div
          className="whitespace-pre-line text-base text-gray-700"
          // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
          dangerouslySetInnerHTML={{ __html: content }}
        />

        {/* MEDIA GALLERY */}
        {mediaItems.length > 0 && (
          <div
            className={`mt-3 relative grid gap-2
              ${
                mediaItems.length === 1
                  ? 'grid-cols-1'
                  : mediaItems.length === 3
                    ? 'grid-cols-2'
                    : 'grid-cols-2'
              }`}
          >
            {isProgram && (
              <Badge
                variant="destructive"
                className="absolute top-2 right-2 text-xs flex items-center justify-center z-[1]"
              >
                {status === 'live' ? 'LIVE' : 'Program'}
              </Badge>
            )}

            {mediaItems.slice(0, 4).map((item, idx) => {
              // เงื่อนไขสำหรับ 3 รูป: สองรูปแรก grid-cols-2, รูปสุดท้าย grid-cols-1
              const isThreeItems = mediaItems.length === 3
              const colSpan = isThreeItems && idx === 2 ? 'col-span-2' : ''

              return (
                <div
                  key={idx}
                  className={`relative group cursor-pointer ${colSpan}`}
                  onClick={() => {
                    if (!isProgram) {
                      // setLightboxIndex(idx)
                      // setLightboxOpen(true)
                    }
                  }}
                >
                  {mediaItems.length > 4 && idx === 3 && (
                    <div className="absolute absolute-center text-5xl text-neutral-300 bg-black/60 w-full h-full flex justify-center items-center rounded-lg">
                      <span>+ {mediaItems.length - 4}</span>
                    </div>
                  )}

                  {item.type === 'image' ? (
                    <img
                      src={
                        sharedData.type === 'livestream'
                          ? item.thumbnailUrl
                          : item.url
                      }
                      alt={`media-${idx}`}
                      className={
                        (mediaItems.length > 1 ? 'h-44' : '') +
                        ' w-full max-h-[1200px] object-cover rounded-lg border border-gray-200'
                      }
                      onError={(e) => {
                        ;(e.currentTarget as HTMLImageElement).src =
                          '/images/image-error.png'
                      }}
                    />
                  ) : (
                    <div
                      className={
                        (mediaItems.length > 1 ? 'h-44' : '') +
                        ' w-full max-h-[1200px] bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200 overflow-hidden'
                      }
                    >
                      <video
                        src={item.url}
                        className="w-full h-full object-cover rounded-lg"
                        controls={false}
                        preload="metadata"
                        onClick={(e) => e.preventDefault()}
                      />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-black/60 rounded-full p-2">
                          <svg
                            className="w-8 h-8 text-white opacity-80"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <polygon
                              points="8,5 19,12 8,19"
                              fill="currentColor"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
        {/* LIGHTBOX DIALOG */}
        {mediaItems.length > 0 && (
          <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
            <DialogTitle />
            <DialogContent
              aria-describedby=""
              className="max-w-2xl px-3 w-[95dvw] flex flex-col items-center justify-center bg-white border border-gray-200"
            >
              <div className="flex flex-col items-center w-full">
                <div
                  className="relative w-full flex items-center justify-center"
                  style={{ minHeight: 320 }}
                >
                  {mediaItems[lightboxIndex]?.type === 'image' ? (
                    <img
                      src={mediaItems[lightboxIndex].url}
                      alt={`media-full-${lightboxIndex}`}
                      className="max-h-[70vh] max-w-full rounded-lg object-contain mx-auto"
                      onError={(e) => {
                        ;(e.currentTarget as HTMLImageElement).src =
                          '/images/image-error.png'
                      }}
                    />
                  ) : (
                    <video
                      src={mediaItems[lightboxIndex].url}
                      controls
                      autoPlay
                      className="max-h-[70vh] max-w-full rounded-lg object-contain mx-auto bg-black"
                    />
                  )}
                  {/* Prev/Next buttons */}
                  {mediaItems.length > 1 && (
                    <>
                      <Button
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 rounded-full p-2 text-white"
                        onClick={(e) => {
                          e.stopPropagation()
                          setLightboxIndex(
                            (lightboxIndex - 1 + mediaItems.length) %
                              mediaItems.length,
                          )
                        }}
                        disabled={mediaItems.length <= 1}
                        aria-label="Previous"
                      >
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </Button>
                      <Button
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 rounded-full p-2 text-white"
                        onClick={(e) => {
                          e.stopPropagation()
                          setLightboxIndex(
                            (lightboxIndex + 1) % mediaItems.length,
                          )
                        }}
                        disabled={mediaItems.length <= 1}
                        aria-label="Next"
                      >
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </Button>
                    </>
                  )}
                </div>
                <div className="mt-2 text-xs text-gray-400">
                  {lightboxIndex + 1} / {mediaItems.length}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
        {/* {feed.hashtags && feed.hashtags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {feed.hashtags.map((tag) => (
              <span
                key={tag}
                className="text-xs font-medium text-red-400 bg-red-400/10 px-2 py-0.5 rounded hover:bg-pink-400/20 cursor-pointer transition-colors"
              >
                #{tag}
              </span>
            ))}
          </div>
        )} */}
      </div>
      {sharedData.type !== 'livestream' && (
        <div className="flex items-center gap-4 mt-2">
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-1 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
          >
            <Heart
              size={18}
              className="text-red-400 transition-transform duration-300"
            />{' '}
            <span className="text-xs transition-transform duration-300">
              {likeCount || 0}
            </span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-1 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
          >
            <MessageCircle size={18} className="text-blue-400" />
            <span className="text-xs">{commentCount}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center gap-1 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
          >
            <Share2 size={18} className="text-green-400" />
            <span className="text-xs">{shareCount}</span>
          </Button>
        </div>
      )}
    </Card>
  )
}
